import { useEffect, useState, useContext } from "react";
import Modal from "~/components/Modal";
import FormGroup from "~/components/FormGroup";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Textarea } from "~/components/ui/textarea";
import { Spinner } from "~/components/ui/spinner";
import styles from "./styles.module.scss";
import { Document, DocumentSearch, LangId } from "~/document";
import DocumentSelect from "~/components/DocumentSelect";
import { observer } from "mobx-react";
import langVersionStore, { DocLangVersion } from "~/stores/langVersionStore";
import cn from "classnames";
import paths from "~/paths";
import FileDropArea from "./FileUploadArea";
import LanguageSelect from "~/components/LanguageSelect";
import languageStore from "~/stores/language";
import { NavLink } from "react-router-dom";
import { ToastNotificationContext } from "~/components/ToastNotificationContext";
import { APIError, apiErrorDetails, consumeLastApiErrorDetails } from "~/api";

interface AddVersionModalProps {
  doc: Document;
  open: boolean;
  close: () => void;
  presentLanguages: Set<LangId>;
}

const AddVersionModal = observer((props: AddVersionModalProps) => {
  const { open, close } = props;
  const { showNotificationWithTimeout, setNotificationData } = useContext(ToastNotificationContext);
  const [title, setTitle] = useState("");
  const [language, setLanguage] = useState<LangId | null>(null);
  const [abstract, setAbstract] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const canSubmit = title.trim().length !== 0 && !!title && language !== null;

  const submit = async () => {
    setSubmitting(true);
    const res = await langVersionStore.addFor(props.doc.protocol, file!, { language: language!, title });
    setSubmitting(false);
    if (res === null) {
      setNotificationData({
        kind: "error",
        title: "Error",
        subtitle: "Language version couldn't be added",
        details: consumeLastApiErrorDetails(),
      });
      showNotificationWithTimeout();
      return;
    }
    setTitle("");
    setLanguage(null);
    setAbstract("");
    setFile(null);
    close();
    setNotificationData({ kind: "success", title: "Success", subtitle: "Language version added successfully" });
    showNotificationWithTimeout();
  };

  return (
    <Modal
      open={open}
      modalLabel="Document versions"
      modalHeading="Add version"
      onRequestClose={close}
      onRequestSubmit={submit}
      primaryButtonDisabled={!canSubmit || submitting}
      primaryButtonText={submitting ? "Adding..." : "Add language version"}
      secondaryButtonText="Cancel"
      className="sm:max-w-[720px]"
    >
      <div className={styles.modal}>
        <FormGroup legendText="Select document">
          <FileDropArea
            className={styles.uploadAreaModal}
            value={file ? [file] : []}
            onAttachFile={(files) => setFile(files[0])}
          />
        </FormGroup>
        <FormGroup legendText="Title">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} />
        </FormGroup>
        <FormGroup legendText="Language">
          <LanguageSelect
            value={language ? [language] : []}
            onChange={(langs) => setLanguage(langs[0])}
            filter={(item) => !props.presentLanguages.has(item.id)}
            selectProps={{ isMulti: false }}
          />
        </FormGroup>
        <FormGroup legendText="Abstract">
          <Textarea value={abstract} onChange={(e) => setAbstract(e.target.value)} />
        </FormGroup>
      </div>
    </Modal>
  );
});

interface LinkVersionModalProps {
  presentLanguages: Set<LangId>;
  doc: Document;
  open: boolean;
  close: () => void;
}

const LinkVersionModal = observer((props: LinkVersionModalProps) => {
  const { open, close } = props;
  const [selectedDoc, setSelectedDoc] = useState<DocumentSearch | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { showNotificationWithTimeout, setNotificationData } = useContext(ToastNotificationContext);
  const canSubmit = selectedDoc !== null;

  const submit = async () => {
    setSubmitting(true);
    try {
      await langVersionStore.linkLangVersion(props.doc.protocol, selectedDoc!);
      setSelectedDoc(null);
      close();
      setNotificationData({ kind: "success", title: "Success", subtitle: "Language version linked successfully" });
    } catch (err) {
      setNotificationData({
        kind: "error",
        title: "Error",
        // APIError carries the server's own message, e.g. "There is already a
        // translation for the selected language".
        subtitle: err instanceof APIError ? err.message : "Language version couldn't be linked",
        details: apiErrorDetails(err),
      });
    } finally {
      setSubmitting(false);
      showNotificationWithTimeout();
    }
  };

  return (
    <Modal
      open={open}
      modalLabel="Document versions"
      modalHeading="Link an existing document"
      onRequestClose={close}
      onRequestSubmit={submit}
      primaryButtonDisabled={!canSubmit || submitting}
      className="overflow-visible"
      primaryButtonText={submitting ? "Linking..." : "Link"}
      secondaryButtonText="Cancel"
    >
      <div className={styles.modal}>
        <FormGroup legendText="Select document">
          <DocumentSelect
            idKey="dbid"
            additionalSearchOptions={{ language__exclude: [...props.presentLanguages].join("__") }}
            onChange={(d: DocumentSearch[]) => setSelectedDoc(d[0])}
            selectProps={{
              isMulti: false,
              filterOption: (option: any, input: string) => {
                const label = option?.label ? option.label.toLowerCase().trim() : "";
                const protocol = option?.data?.protocol ? option.data.protocol.toLowerCase().trim() : "";
                return label.includes(input.toLowerCase()) || protocol.includes(input.toLowerCase());
              },
            }}
            value={selectedDoc ? [selectedDoc.dbid] : []}
          />
        </FormGroup>
      </div>
    </Modal>
  );
});

export const DocumentLangVersion = observer(({ item, protocol }: { item: DocLangVersion; protocol: string }) => {
  const [loading, setLoading] = useState(false);
  const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);
  const { showNotificationWithTimeout, setNotificationData } = useContext(ToastNotificationContext);

  const remove = async () => {
    setLoading(true);
    setConfirmationModalVisible(false);
    const res = await langVersionStore.deleteLangVersion(protocol, item.protocol);
    setLoading(false);
    if (res === null) {
      setNotificationData({
        kind: "error",
        title: "Error",
        subtitle: "Language version couldn't be removed",
        details: consumeLastApiErrorDetails(),
      });
    } else {
      setNotificationData({ kind: "success", title: "Success", subtitle: "Language version removed" });
    }
    showNotificationWithTimeout();
  };

  return (
    <div className={cn(["flex flex-row justify-between w-full", styles.listItem])}>
      <div className="w-full">
        <div className="w-full">
          <NavLink className="text-sm text-primary-600 hover:underline" to={paths.construct.docDetail(item.protocol)}>
            {`${item.title} (${languageStore.idToText(item.language)})`}
          </NavLink>
          <div className={styles.listItemProtocol + " text-xs [color:var(--text-02)]"}>{item.protocol}</div>
        </div>
      </div>
      <div className="flex flex-row items-start">
        {loading && <Spinner className="mr-2 size-4" />}
        <Button
          disabled={loading}
          onClick={() => setConfirmationModalVisible(true)}
          size="sm"
          variant="destructive"
          className="text-xs"
        >
          Remove
        </Button>
      </div>
      <Modal
        open={confirmationModalVisible}
        modalHeading="Are you sure?"
        onRequestSubmit={remove}
        onRequestClose={() => setConfirmationModalVisible(false)}
        onSecondarySubmit={() => setConfirmationModalVisible(false)}
        secondaryButtonText="Cancel"
        primaryButtonText="I am sure"
      >
        <div>
          You are about to delink this language version from others. This will not delete the document, only unlink it.
        </div>
        <div className="font-bold">Do you want to continue?</div>
      </Modal>
    </div>
  );
});

const DocumentLangVersionSelect = observer(({ doc, showModal, setShowModal }: any) => {
  const [showLinkModal, setShowLinkModal] = useState(false);
  const versions = langVersionStore.loaded.get(doc.protocol) ?? [];

  useEffect(() => {
    if (doc.protocol) langVersionStore.versionsFor(doc.protocol);
  }, [doc.protocol]);

  // Not memoized: the store mutates its version array in place, so an identity-based
  // memo would keep rendering a stale list after an add/link.
  const renderedVersions =
    versions.length === 0 ? (
      <div className="[color:var(--text-02)] text-sm">No language versions linked</div>
    ) : (
      versions.map((item) => <DocumentLangVersion key={`lang-${item.uuid}`} protocol={doc.protocol} item={item} />)
    );

  const presentLanguages = new Set([doc.language, ...versions.map((a) => a.language)]);

  return (
    <div>
      <div className="flex justify-between flex-row items-center">
        <div className="text-sm">
          <strong>Language versions</strong>
        </div>
        <div>
          <Button variant="default" onClick={() => setShowModal(true)} size="sm" className="mr-2 text-xs">
            Add language version
          </Button>
          <Button variant="secondary" onClick={() => setShowLinkModal(true)} size="sm" className="text-xs">
            Link
          </Button>
        </div>
      </div>
      <div className={styles.relDocsList}>{renderedVersions}</div>
      <AddVersionModal
        presentLanguages={presentLanguages}
        open={showModal}
        close={() => setShowModal(false)}
        doc={doc}
      />
      <LinkVersionModal
        presentLanguages={presentLanguages}
        open={showLinkModal}
        close={() => setShowLinkModal(false)}
        doc={doc}
      />
    </div>
  );
});

export default DocumentLangVersionSelect;
